import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { routeName } from 'routes/routes-name'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/**API */
import ClinicApi from 'api/master/clinic.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'
import { getBranch, numberFormat, toBuddhistYear } from 'utils/app.utils'

/**COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import { useDownloadExcel } from 'component/Excel/hooks/useExcel'
import ExportToExcel from './ExportToExcel'

/**STYLE */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import Loading from 'component/Loading'
import { colors } from 'constants/theme'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import { dateToView, timeToView } from 'utils/date.utils'
import { UseSnCode } from 'utils/useSnCode'
import AdvanceDecreaseList from './DecreaseList'

/**STYLE */


type Doctors = {
  userId: number
  firstname: string
  lastname: string
  fullname: string
}

export interface DentistInterface {
  dentistId: number
  dentistFullname: string
}

export default function AdvanceDecrease() {
  const { t } = useTranslation()
  const history = useHistory()
  const componentRef = useRef<HTMLDivElement>(null)

  const momentNowRange = [moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
  const momentDate = () => momentNowRange

  const [branchId, setBranchId] = useState<any>(getBranch())
  const [branches, setBranches] = useState<any[]>([])
  const [search, setSearch] = useState<string>('')
  const [rangeDate, setRangeDate] = useState<string[]>(momentDate)
  const [billStatus, setBillStatus] = useState<string>('ALL')

  const [rowCount, setRowCount] = useState<number>(0)
  const [sortBy, setSortBy] = useState<string>('createAt')
  const [sortType, setSortType] = useState<string>('ASC')
  const [fetch, setFetch] = useState(false)
  const [rows, setRows] = useState<any[]>([])
  const [rowsExport, setRowsExport] = useState<any[]>([])
  const [page, setPage] = useState<number>(1)
  const [pageLimit, setPageLimit] = useState<number>(10)
  const [loading, setLoading] = useState<boolean>(true)
  const [snType, setSnType] = useState(false)

  const handleSnCode = useCallback(() => {
    const fetchData = async () => {
      const sn = await UseSnCode()
      setSnType(sn)
    }
    fetchData()
  }, [snType])

  useEffect(() => {
    handleSnCode()
  }, [])

  const verifyPermission = async () => {
    const condition: FindAllReportInterface = {
      page: 1,
      pageLimit: 200
    }
    ReportApi.reportList(condition).then(({ data }) => {
      const check = _.find(data, (val: any) => String(val.reportKey) === 'ADVANCE_DECREASE') ? true : false
      if (!check) {
        history.push(routeName.report)
        return false
      }
    })

    ClinicApi.findAllBranches()
      .then(({ data }) => setBranches(data))
      .catch((e) => {
        return
      })

    return true
  }

  useEffect(() => {
    verifyPermission()
  }, [branchId])

  const fetchDF = useCallback(async () => {
    let condition: any = {
      page,
      pageLimit,
      branchId,
      dateStart: rangeDate[0],
      dateEnd: rangeDate[1],
      search
    }

    if (billStatus && billStatus !== 'ALL') condition = { ...condition, billStatus: billStatus }

    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }

    ReportApi.reportAdvanceDecrease(condition)
      .then(({ headers, data }) => {
        setRows(data.rows)
        setRowsExport(data.rowsExport)
        setRowCount(headers['x-total'])
      })
      .catch((e) => setLoading(false))
      .finally(() => {
        setLoading(false)
      })
    setFetch(false)
  }, [page, pageLimit, sortBy, sortType, fetch])

  const { onDownload } = useDownloadExcel({
    tableExportRef: componentRef.current,
    filename: `${t(`REPORT.REPORT_NAME.ADVANCE_DECREASE`)}`,
    sheetName: `${t(`REPORT.REPORT_NAME.ADVANCE_DECREASE`)}`
  })

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangeRowsPerPage = (limit: number) => setPageLimit(limit)

  const handleChangePage = (val: number) => setPage(val)

  const exportExcel = async () => {
    setLoading(true);
    try {
      const branchName = branchId === 0
        ? t('REPORT.FILTER.ALL')
        : _.get(_.find(branches, { branchId: branchId }), 'branchName', '');
      await ExportToExcel({
        data: rowsExport,
        branchName: branchName,
        rangeDate: rangeDate,
        billStatus: billStatus,
        t: t,
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchDF()
  }, [fetchDF])

  const statusColor: any = {
    PAID: { color: colors.appointmentStatus.statusEnter },
    CANCEL_VISIT: { color: colors.appointmentStatus.statusCancel },
    CANCEL_BILL: { color: '#F47C00' },
    OVERDUE: { color: colors.appointmentStatus.statusUnConnect }
  }

  return (
    <Box>
      <Loading show={loading} />
      <HeaderCard text={t(`REPORT.REPORT_NAME.ADVANCE_DECREASE`)} />
      <Box padding="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={2}>
            <TextInput
              label={t('INPUT.SEARCH_PATIENT')}
              value={search}
              fullWidth
              onChange={(event) => setSearch(event.target.value)}
              inputProps={{
                value: search,
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SelectInput
              onChange={(e) => {
                setBranchId(e.target.value)
                setPage(1)
              }}
              options={_.map(branches, (item) => ({ value: item.branchId, name: item.branchName }))}
              value={branchId}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2} >
            <InputSecondNewRangePicker
              inputHeight={40}
              value={rangeDate || emptyRangePicker}
              onchange={(val: any) => {
                if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
              }}
              label={''}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SelectInput
              onChange={(e) => {
                setBillStatus(e.target.value)
                setPage(1)
              }}
              options={[
                { value: "ALL", name: t('REPORT.FILTER.ALL') },
                { value: "OVERDUE", name: t('REPORT.BILL_STATUS.OVERDUE') },
                { value: "PAID", name: t('REPORT.BILL_STATUS.PAID') },
                { value: "CANCEL_BILL", name: t('REPORT.BILL_STATUS.CANCEL_BILL') },
              ]}
              value={billStatus}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <MainButton
                title={t('INPUT.SEARCH')}
                onClick={() => {
                  setFetch(true)
                  setPage(1)
                  setLoading(true)
                }}
                type={'primary'}
                variant={'contained'}
                style={{ width: '120px' }}
              />
              <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                if (e === 'Excel') {
                  exportExcel();
                } else if (e === 'PDF') {
                  const style = document.createElement("style");
                  style.innerHTML = `
                      @media print {
                        @page { size: landscape }
                      }
                    `;
                  document.head.appendChild(style);
                  window.print();
                  document.head.removeChild(style);

                }
              }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
            </Box>
          </Grid>
        </Grid>
        <Box marginTop="16px">
          <AdvanceDecreaseList
            page={page}
            pageLimit={pageLimit}
            rowCount={rowCount}
            sortBy={sortBy}
            sortType={sortType}
            data={rows}
            dataExport={rowsExport}
            onRequestSort={onRequestSort}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleChangePage={handleChangePage}
            setSortType={setSortType}
            setSortBy={setSortBy}
            componentRef={componentRef}
            branchName={_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn')}
            rangeDate={rangeDate}
            billStatus={billStatus}
          />
        </Box>
      </Box>
      <Box
        id="print-table-container"
        ref={componentRef}
      >
        <Typography fontSize="14px" fontWeight={400}>{t(`REPORT.REPORT_NAME.ADVANCE_DECREASE`)}</Typography>
        <Box display='flex' flexDirection="row" alignItems="center" gap="16px">
          <Typography fontSize="10px" fontWeight={400}>{t('REPORT.TABLE.DECREASE.BRANCH')}: {_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn')}</Typography>
          <Typography fontSize="10px" fontWeight={400}>{t('REPORT.TABLE.DECREASE.DATE_START')}: {toBuddhistYear(moment(rangeDate[0]))}</Typography>
          <Typography fontSize="10px" fontWeight={400}>{t('REPORT.TABLE.DECREASE.DATE_END')}: {toBuddhistYear(moment(rangeDate[1]))}</Typography>
          <Typography fontSize="10px" fontWeight={400}>{t('REPORT.TABLE.DECREASE.STATUS')}: {`${billStatus === 'ALL' ? t(`REPORT.FILTER.ALL`) : t(`REPORT.FILTER.BILL.${billStatus}`)}`}</Typography>
        </Box>
        <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '16px' }}>
          <thead>
            <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
              <th style={{ borderBottom: '1px solid black' }}>{t('REPORT.TABLE.DF.#NO')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('REPORT.TABLE.DECREASE.BRANCH')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('REPORT.TABLE.DECREASE.DATE_ADVANCE')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('REPORT.TABLE.DECREASE.BILL')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('REPORT.TABLE.DECREASE.STATUS')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('REPORT.TABLE.DECREASE.PATIENT_CN_NUMBER')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('REPORT.TABLE.DECREASE.BRANCH_CN_NUMBER')}</th>
              {snType && <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.DECREASE.PATIENT_SN_CODE')}</th>}
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.DECREASE.PATIENT_NAME')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('REPORT.TABLE.DECREASE.TOTAL_BILL')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('REPORT.TABLE.DECREASE.TOTAL_DF')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('REPORT.TABLE.DECREASE.TOTAL_ADVANCE')}</th>
            </tr>
          </thead>
          <tbody>
            {rowsExport.map((data: any, index: number) => (
              <tr key={index}>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '50px' }}>{index + 1}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '150px' }}>{data?.branchName}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '150px' }}>{dateToView(data.createdAt)} <br /> {timeToView(data.createdAt)} น.</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '100px' }}>{data.stage > 1 ? (
                  <span>
                    {data.paymentNumber} <br /> <span style={{ color: 'red' }}> {t('REPORT.TABLE.REMUNERATION.IS_PAY_OVERDUE')}</span>
                  </span>
                ) : (
                  data.paymentNumber || '-'
                )}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', ...statusColor[data.statusOrder], width: '100px' }}>{t(`REPORT.TABLE.ADVANCE.STATUS.${data.statusOrder}`)}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '85px' }}>{data?.cnNumber}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '85px' }}>{data?.branchCnNumber}</td>
                {snType && <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top', width: '85px' }}>{data?.snCode}</td>}
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top' }}>{data?.patientFullname}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top', width: '85px' }}>{numberFormat(data.totalPrice, 2)}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top', width: '85px' }}>{numberFormat(data.amountPaid, 2)}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top', width: '85px' }}>{numberFormat(data.amountAdvance, 2)}</td>
              </tr>
            ))}
            <tr style={{ border: '1px solid black', backgroundColor: '#E0E0E0' }}>
              <td colSpan={snType ? 7 : 8} style={{ fontSize: '9.5px', fontWeight: 500 }} align='right'>รวม</td>
              <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top' }}>{numberFormat(_.sumBy(rowsExport, 'totalPrice'))}</td>
              <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top' }}>{numberFormat(_.sumBy(rowsExport, 'amountPaid'))}</td>
              <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top' }}>{numberFormat(_.sumBy(rowsExport, 'amountAdvance'))}</td>
            </tr>
          </tbody>
        </table>
      </Box>
    </Box>
  )
}
